import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";

import { Spacer, VStack } from "@/components/Layout";
import type { NavigationSectionGroup } from "@/components/Navigation/NavigationCard";
import { CForm } from "@/features/c-form/CForm";
import useZodValidation from "@/hooks/useZodValidation";
import type { ApiError } from "@/lib/api/error";
import { defaultNS, useTranslation } from "@/lib/i18n";

import { useDevice, useUpdateDevice } from "../../../-query";
import { mapGetDeviceToUpdateDevice } from "../../../-query/selector";
import { LeaseContractSection } from "../index/-components/sections/lifecycle/LeaseContract";
import { LogsAuditSection } from "../index/-components/sections/lifecycle/LogsAudit";
import { MaintenanceRepairSection } from "../index/-components/sections/lifecycle/MaintenanceRepair";
import { PlanningBudgetingSection } from "../index/-components/sections/lifecycle/PlanningBudgeting";
import { ProcurementAcquisitionSection } from "../index/-components/sections/lifecycle/ProcurementAcquisition";
import { ProvisioningDeploymentSection } from "../index/-components/sections/lifecycle/ProvisioningDeployment";
import { RetirementDisposalSection } from "../index/-components/sections/lifecycle/RetirementDisposal";
import { TransportAndMovementSection } from "../index/-components/sections/lifecycle/TransportAndMovement";
import { UpgradeRedeploymentSection } from "../index/-components/sections/lifecycle/UpgradeRedeployment";
import { UsageOperationSection } from "../index/-components/sections/lifecycle/UsageOperation";
import { OrderStatusBanner } from "../index/-components/shared/OrderStatusBanner";
import { EditLifecycleFormStore } from "../index/-store/edit-lifecycle-store";
import {
  EditDeviceFormStore,
  useEditDeviceFormStore,
} from "../index/-store/store";

export const Route = createFileRoute(
  "/_staff/profile/device/$id/_profile/lifecycle/",
)({
  component: DeviceProfile,
});

function DeviceProfile() {
  const store = EditDeviceFormStore.createStore();
  const lifecycleStore = EditLifecycleFormStore.createStore();
  return (
    <EditDeviceFormStore.Provider value={store}>
      <EditLifecycleFormStore.Provider value={lifecycleStore}>
        <Content />
      </EditLifecycleFormStore.Provider>
    </EditDeviceFormStore.Provider>
  );
}

function Content() {
  useZodValidation();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.devices.profile.navigation",
  });

  const { id } = Route.useParams();
  const { data } = useDevice(id);
  const { values, actions } = useEditDeviceFormStore();
  const { mutateAsync: updateDevice } = useUpdateDevice(id, values);
  /* oxlint-disable react-hooks/exhaustive-deps -- actions.resetは安定した参照 */
  useEffect(
    () => actions.reset(mapGetDeviceToUpdateDevice(data)),
    [actions.reset, data],
  );
  /* oxlint-enable react-hooks/exhaustive-deps */

  const navigationSectionGroups: NavigationSectionGroup[] = [
    {
      titleLabel: t("audit"),
      to: "",
      sections: [
        {
          label: t("sectionTitle.logsAudit"),
          hash: "logs-audit",
          //TODO: アイコンの線が太い
          icon: "QuickReferenceAll",
        },
      ],
    },
    {
      //TODO: アイコンが全体的に小さい？
      titleLabel: t("lifecycle"),
      to: "",
      sections: [
        {
          label: t("sectionTitle.provisioningDeployment"),
          hash: "provisioning-deployment",
          icon: "RedoDotIcon",
        },
        {
          label: t("sectionTitle.transportAndMovement"),
          hash: "transport-and-movement",
          icon: "Truck",
        },
        {
          label: t("sectionTitle.upgradeRedeployment"),
          hash: "upgrade-redeployment",
          icon: "RecycleIcon",
        },
        {
          label: t("sectionTitle.maintenanceRepair"),
          hash: "maintenance-repair",
          icon: "WrenchIcon",
        },
        {
          label: t("sectionTitle.retirementDisposal"),
          hash: "retirement-disposal",
          icon: "Trash2Icon",
        },
      ],
    },
    {
      titleLabel: t("planning"),
      to: "",
      sections: [
        {
          label: t("sectionTitle.planningBudgeting"),
          hash: "planning-budgeting",
          icon: "ShoppingCart",
        },
        {
          label: t("sectionTitle.procurementAcquisition"),
          hash: "procurement-acquisition",
          icon: "Contract",
        },
        {
          label: t("sectionTitle.usageOperation"),
          hash: "usage-operation",
          icon: "IterationCw",
        },
      ],
    },
    {
      titleLabel: t("contract"),
      to: "",
      sections: [
        {
          label: t("sectionTitle.leaseContract"),
          hash: "lease-contract",
          icon: "ContractProductLogoSmall",
        },
      ],
    },
  ];

  const { mutateAsync: onSave, isPending } = useMutation({
    mutationFn: async () => {
      if (!actions.validate()) return false;
      return await updateDevice();
    },
    onError: (error: ApiError) => {
      actions.onApiError(error);
    },
  });

  return (
    <VStack className="h-full">
      {data.base.status === "IN_DISTRIBUTION" && (
        <OrderStatusBanner
          checkOrderLinkUrl={`/marketplace/sent-orders/${data.base.marketplaceOrderId}`}
        />
      )}

      <CForm
        editUnit="section"
        itemLayout="label"
        navigationSectionGroups={navigationSectionGroups}
        onSave={onSave}
        onReset={() => actions.reset(mapGetDeviceToUpdateDevice(data))}
      >
        <LogsAuditSection isPending={isPending} />
        <ProvisioningDeploymentSection isPending={isPending} />
        <TransportAndMovementSection isPending={isPending} />
        <UpgradeRedeploymentSection isPending={isPending} />
        <MaintenanceRepairSection />
        <RetirementDisposalSection />
        <Spacer size={16} />
        <PlanningBudgetingSection isPending={isPending} />
        <ProcurementAcquisitionSection />
        <UsageOperationSection />
        <LeaseContractSection />
      </CForm>
    </VStack>
  );
}
